<template>
    <BaseCard class="flex flex-col" padding="none">
        <div class="flex flex-col gap-2 p-6">
            <h3
                ref="heading"
                tabindex="-1"
                class="nadia-subtitle text-ink rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-control-accent focus-visible:ring-offset-2">
                {{ t("insights.profileLinks.card.heading") }}
            </h3>
            <p class="body-small text-ink-muted">
                {{ showTeamSuggestions ? t("insights.teamSuggestions.intro") : t("insights.profileLinks.card.intro")
                }}<template v-if="canComparePerspectives">{{ " " + t("insights.profileLinks.card.comparePerspectives") }}</template>
            </p>
        </div>

        <Transition name="sharpen-swap" mode="out-in">
            <div v-if="isLoading && !loadedSuggestions" class="border-t border-border p-6">
                <SkeletonLoader class="h-10 w-full rounded-full" :aria-label="t('insights.profileLinks.card.loadingLabel')" />
            </div>

            <div v-else-if="loadFailed" class="border-t border-border p-6">
                <div
                    role="alert"
                    class="flex flex-col items-center gap-2 rounded-lg border border-border bg-surface-sunken px-4 py-3 text-center"
                    data-test-id="profile-link-load-error">
                    <p class="body-small text-ink-muted">{{ t("insights.profileLinks.card.loadError") }}</p>
                    <BaseButton theme="secondary" size="sm" :disabled="isLoading" data-test-id="profile-link-retry-btn" @click="reload">{{ t("common.tryAgain") }}</BaseButton>
                </div>
            </div>

            <div v-else-if="showCelebration" class="border-t border-border p-6">
                <div class="flex items-center gap-3 rounded-lg border border-border px-4 py-3">
                    <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-ink-inverse">
                        <i class="bi bi-check-lg text-base/none icon-semibold" aria-hidden="true"></i>
                    </span>
                    <div>
                        <p class="body-small text-ink-muted">
                            <span class="body-small-bold text-ink">{{
                                showTeamSuggestions ? t("insights.teamSuggestions.peopleCaughtUp") : t("insights.profileLinks.card.caughtUp")
                            }}</span>
                        </p>
                        <p class="body-small text-ink-muted">{{ t("insights.profileLinks.card.refreshHint") }}</p>
                    </div>
                </div>
            </div>

            <ProfileLinkSuggestionsInline
                v-else-if="loadedSuggestions"
                :suggestions="inlineRows"
                :statuses="effectiveStatuses"
                :own-profile-share="ownProfileShare"
                :org-has-ms-teams="orgHasMsTeams"
                share-source="my_work_modal"
                :profile-link-sharing-enabled="profileLinkSharingEnabled"
                :can-link-all="canLinkAll"
                :is-linking-all="isLinkingAll"
                :link-all-blocked="pendingDismissIds.size > 0"
                :disabled="isLinkingAll || isLoading"
                @linked="handleLinked"
                @link-failed="handleLinkFailed"
                @shared="handleShared"
                @share-failed="handleShareFailed"
                @dismiss-started="handleDismissStarted"
                @dismissed="handleDismissed"
                @dismiss-failed="handleDismissFailed"
                @focus-fallback="focusHeading"
                @link-all="handleLinkAll"
                @create-profile-shown="emit('create-profile-shown')"
                @create-profile-click="emit('create-profile-click')"
                @explainer-click="logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_EXPLAINER_CLICKED, { surface })" />
        </Transition>

        <TeamSuggestionsSection v-if="showTeamSuggestions" :team-url-template="teamUrlTemplate ?? ''" />

        <p class="sr-only" role="status">{{ dismissAnnouncement }}</p>
        <BaseToast v-model:open="isToastOpen" :theme="toastTheme" :description="toastMessage" :duration="toastDuration" />
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import { logProfileShared } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import ProfileLinkSuggestionsInline from "~vue/Labs/ProfileLinkSuggestionsInline.vue";
import TeamSuggestionsSection from "~vue/Labs/TeamSuggestionsSection.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import type {
    BulkCollabProfileLinkRequest,
    BulkCollabProfileLinkResult,
    OwnProfileShare,
    ProfileLinkSuggestion,
    ProfileLinkSuggestionsResponse,
    ShareFailureReason,
    ShareTarget,
    SuggestionStatus,
} from "./profileLinkTypes";

const { t } = useI18n();
const { canComparePerspectives } = useProfileSharingCopy();

const props = defineProps<{
    teamsEnabled?: boolean;
    teamUrlTemplate?: string;
    initialLinkedCount: number;
    ownProfileShare: OwnProfileShare | null;
    orgHasMsTeams: boolean;
    profileLinkSharingEnabled: boolean;
    surface: string;
}>();

const emit = defineEmits<{
    "create-profile-shown": [];
    "create-profile-click": [];
}>();

const {
    execute: fetchSuggestions,
    data: suggestionsData,
    error: loadError,
} = useFetch("/v1/connections/profile-link-suggestions", { immediate: false }).get().json<ProfileLinkSuggestionsResponse>();

const loadedSuggestions = ref<ProfileLinkSuggestionsResponse | null>(null);
const hasLoaded = ref(false);
const loadFailed = ref(false);
const isLoading = ref(false);

const bulkLinkPayload = ref<BulkCollabProfileLinkRequest>();
const {
    data: bulkLinkData,
    error: bulkLinkError,
    statusCode: bulkLinkStatus,
    isFetching: isBulkLinking,
    execute: executeBulkLink,
} = useFetch("/v1/collaboration-profiles/bulk-ensure-link", { immediate: false }).post(bulkLinkPayload).json<BulkCollabProfileLinkResult>();

const statuses = ref<Record<string, SuggestionStatus>>({});
const linkedDelta = ref(0);
const dismissedIds = ref<Set<string>>(new Set());
const pendingDismissIds = ref<Set<string>>(new Set());
const dismissAnnouncement = ref("");

const isSettlingLinkAll = ref(false);
const isLinkingAll = computed(() => isBulkLinking.value || isSettlingLinkAll.value);

const showTeamSuggestions = computed(() => props.teamsEnabled && !!props.teamUrlTemplate);
const suggestions = computed(() => loadedSuggestions.value?.suggestions ?? []);
const inlineRows = computed(() => suggestions.value.filter((s) => statuses.value[s.target_user_id] !== "linked" && !dismissedIds.value.has(s.target_user_id)));
const canLinkAll = computed(() => inlineRows.value.some((s) => s.action === "link"));
const linkedCount = computed(() => (loadedSuggestions.value?.linked_count ?? props.initialLinkedCount) + linkedDelta.value);

const effectiveStatuses = computed<Record<string, SuggestionStatus>>(() =>
    Object.fromEntries(suggestions.value.map((s) => [s.target_user_id, statuses.value[s.target_user_id] ?? "idle"])),
);

const openCount = computed(() => suggestions.value.filter((s) => effectiveStatuses.value[s.target_user_id] === "idle").length);
const showCelebration = computed(() => hasLoaded.value && !loadFailed.value && !isLoading.value && !isLinkingAll.value && inlineRows.value.length === 0);

const isToastOpen = ref(false);
const toastMessage = ref("");
const toastTheme = ref<"primary" | "error">("primary");
const toastDuration = ref(5000);
const showToast = (message: string, theme: "primary" | "error" = "primary", duration = 5000) => {
    toastMessage.value = message;
    toastTheme.value = theme;
    toastDuration.value = duration;
    isToastOpen.value = true;
};

async function reload() {
    isLoading.value = true;
    try {
        await fetchSuggestions();
        hasLoaded.value = true;
        loadFailed.value = loadError.value !== null || suggestionsData.value == null;
        if (loadFailed.value) {
            return;
        }
        loadedSuggestions.value = suggestionsData.value;
        linkedDelta.value = 0;
        statuses.value = {};
        dismissedIds.value = new Set();
        pendingDismissIds.value = new Set();
    } finally {
        isLoading.value = false;
    }
}

onMounted(async () => {
    await reload();
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_CARD_SHOWN, {
        linked_count: linkedCount.value,
        suggestion_count: openCount.value,
        surface: props.surface,
    });
});

// The all-caught-up state is a strong activation signal. Fire once per batch clear.
const celebrationLogged = ref(false);
watch(showCelebration, (visible) => {
    if (visible && !celebrationLogged.value) {
        celebrationLogged.value = true;
        logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_CELEBRATION_SHOWN, {
            linked_count: linkedCount.value,
            surface: props.surface,
        });
    }
});

function markLinked(targetUserId: string) {
    if (statuses.value[targetUserId] === "linked") {
        return;
    }
    statuses.value[targetUserId] = "linked";
    linkedDelta.value += 1;
}

function handleLinked(suggestion: ProfileLinkSuggestion) {
    markLinked(suggestion.target_user_id);
    showToast(t("insights.profileLinks.card.linked", { name: suggestion.name }));
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_LINKED, { source: suggestion.source, surface: props.surface });
}

function handleLinkFailed(suggestion: ProfileLinkSuggestion) {
    showToast(t("insights.profileLinks.card.linkFailed"), "error");
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_LINK_FAILED, { source: suggestion.source, surface: props.surface });
}

function settleDismiss(targetUserId: string) {
    const pending = new Set(pendingDismissIds.value);
    pending.delete(targetUserId);
    pendingDismissIds.value = pending;
}

const heading = useTemplateRef<HTMLElement>("heading");
async function focusHeading() {
    await nextTick();
    heading.value?.focus();
}

function handleDismissStarted(suggestion: ProfileLinkSuggestion) {
    pendingDismissIds.value = new Set(pendingDismissIds.value).add(suggestion.target_user_id);
}

function handleDismissed(suggestion: ProfileLinkSuggestion) {
    settleDismiss(suggestion.target_user_id);
    dismissedIds.value = new Set(dismissedIds.value).add(suggestion.target_user_id);
    dismissAnnouncement.value = t("insights.profileLinks.card.dismissed", { name: suggestion.name });
}

function handleDismissFailed(suggestion: ProfileLinkSuggestion) {
    settleDismiss(suggestion.target_user_id);
    showToast(t("insights.profileLinks.card.dismissFailed"), "error");
}

async function handleLinkAll() {
    // A dismissal still in flight hasn't left inlineRows yet; linking now could link that person.
    if (isLinkingAll.value || pendingDismissIds.value.size > 0) {
        return;
    }
    const batch = inlineRows.value.filter((s) => s.action === "link" && effectiveStatuses.value[s.target_user_id] === "idle");
    if (batch.length === 0) {
        return;
    }

    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_LINK_ALL_CLICKED, { count: batch.length, surface: props.surface });

    const connectionIdsByTargetUserId = batch.reduce<Record<string, string>>((connectionIds, suggestion) => {
        if (suggestion.connection_id) {
            connectionIds[suggestion.target_user_id] = suggestion.connection_id;
        }
        return connectionIds;
    }, {});
    bulkLinkPayload.value = {
        target_user_ids: batch.map((s) => s.target_user_id),
        connection_ids_by_target_user_id: connectionIdsByTargetUserId,
    };
    isSettlingLinkAll.value = true;
    try {
        await executeBulkLink();

        const result = bulkLinkError.value || bulkLinkStatus.value === null ? null : bulkLinkData.value;
        if (!result) {
            showToast(t("insights.profileLinks.card.linkAllFailed"), "error");
            return;
        }

        // Marking these linked empties the list, which fades the section out (see <Transition>).
        for (const targetUserId of result.succeeded) {
            markLinked(targetUserId);
        }

        // Failed rows keep their Link button, so the toast is the only extra signal the user gets.
        if (result.failed.length > 0) {
            showToast(t("insights.profileLinks.card.linkAllFailed"), "error");
        } else if (result.succeeded.length > 0) {
            const count = result.succeeded.length;
            showToast(t("insights.profileLinks.card.linkedMany", { count }, count));
        }
    } finally {
        isSettlingLinkAll.value = false;
    }
}

function handleShared(suggestion: ProfileLinkSuggestion, target: ShareTarget) {
    statuses.value[suggestion.target_user_id] = "shared";
    if (target === "link") {
        showToast(t("insights.profileLinks.card.copiedInvite", { firstName: firstNameOf(suggestion.name) }), "primary", 10000);
    }
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_SHARED, { source: suggestion.source, target, surface: props.surface });
    void logProfileShared({ shareSource: "my_work_modal", target, shareKind: "own_profile", access: props.ownProfileShare?.access, suggestionOrigin: suggestion.source });
}

const SHARE_FAILURE_MESSAGES: Record<ShareFailureReason, () => string> = {
    not_publishable: () => t("insights.profileLinks.card.shareFailure.notPublishable"),
    invite: () => t("insights.profileLinks.card.shareFailure.invite"),
    clipboard: () => t("insights.profileLinks.card.shareFailure.clipboard"),
};

function handleShareFailed(suggestion: ProfileLinkSuggestion, reason: ShareFailureReason) {
    showToast(SHARE_FAILURE_MESSAGES[reason](), "error");
    // An unpublished profile is a setup step the user has to complete, not a failed request.
    if (reason !== "not_publishable") {
        logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_REQUEST_FAILED, { source: suggestion.source, surface: props.surface, reason });
    }
}
</script>

<style scoped>
/* Fade the suggestions out (and the all-caught-up state in) when Link all clears the list. */
@media (prefers-reduced-motion: no-preference) {
    .sharpen-swap-enter-active,
    .sharpen-swap-leave-active {
        transition: opacity 250ms ease;
    }

    .sharpen-swap-enter-from,
    .sharpen-swap-leave-to {
        opacity: 0;
    }
}
</style>
